// sync-status-banner.js — say it out loud when writes are not reaching the server.
//
// A-454. Cloud sync is gated by TWO independent conditions, and until both clear
// every write is queued locally and silently dropped:
//
//   * the E2E vault  — js/supabase-sync.js `_strictCryptoSkip()`
//   * MFA            — js/sync-facade.js `writeBlocked()` → SupabaseAuth.mfaRequired()
//
// `Sync.schedulePush()` marks the doc dirty and returns `undefined` (sync-facade
// .js:76-83). No toast, no error, no badge. Measured on a real invited account:
// it created a reminder and a saved quote, and its `user_blobs` row count stayed
// at ZERO while the owner's sat at 27. The agent had no way to know.
//
// That is the failure this file exists to make impossible. It is deliberately
// NOT dismissible: a dismissed warning about silent data loss is the same bug
// wearing a hat. It is also deliberately not a toast — toasts expire, and this
// condition persists until someone acts on it.
//
// It doubles as the missing ENTRY POINT. `AuthMFAUI.openEnroll()` has existed
// all along with exactly one caller — a bare `setTimeout(…, 5000)` in
// app-boot.js that is suppressed on a new member's first boot. There is no
// button for it anywhere in the app (the one in index.html called
// `MigrationUI.handleEnableMfa()`, and js/migration-ui.js does not exist). So
// the banner's buttons are, today, the only reliable way a new hire reaches
// either setup flow.
//
// Reads SecurityPosture.getSnapshot() rather than re-deriving anything — that is
// already the one place both gates are evaluated together. Note its `cloud.label`
// reports only the FIRST blocker it hits (E2E is checked before MFA), which is
// why this renders from the underlying `vault` / `mfa` facts instead of the
// label: a member who fixes the vault should not then discover a second wall.

window.SyncStatusBanner = (() => {
    'use strict';

    const BANNER_ID = 'syncBlockedBanner';
    // Backstop poll. The PRIMARY signal is the app-wide `vault:unlocked` event
    // (see init) — an earlier version of this comment claimed no such event
    // existed and leaned on the poll alone, which was simply wrong: VaultUI
    // dispatches it and 16 modules already listen for it.
    const POLL_MS = 20000;
    // A blocked reading must be seen TWICE, this far apart, before anything is
    // shown. Both inputs hydrate ASYNCHRONOUSLY after boot:
    //   * CryptoHelper.isV2Unlocked() reads an in-memory `_v2Key` that the
    //     stay-unlocked-on-device path restores from IndexedDB.
    //   * SupabaseAuth.hasVerifiedFactor() reads a `_factors` cache that
    //     _refreshFactors() fills from an async listFactors() call.
    // So at init() a perfectly healthy returning user reads as blocked on BOTH
    // counts. Rendering immediately would flash "your work is only saved on this
    // device" on every single reload, and a banner that cries wolf daily is
    // ignored within a week — which is the exact silence this file exists to
    // end. A transient boot state clears itself before the confirm; a genuine
    // block persists. This is deliberately state-shaped rather than a list of
    // hydration hooks, so a future async source cannot reintroduce the flash.
    const CONFIRM_MS = 6000;

    let _timer = null;
    let _confirmTimer = null;
    let _lastSig = '';
    let _pendingSig = '';

    const esc = (s) => (window.Utils && typeof Utils.escapeHTML === 'function')
        ? Utils.escapeHTML(s)
        : String(s == null ? '' : s).replace(/[&<>"']/g, c => (
            { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));

    /**
     * True while another gate owns the screen, or while the vault is still
     * restoring. Three body classes matter, all of which hide top-level chrome
     * behind a direct-child allow-list this banner is not on:
     *
     *   body.vault-restoring — the on-device key is being restored from
     *       IndexedDB. Until it lands, `isV2Unlocked()` reads false, so ANY
     *       assessment made here is a guess about a question still in flight.
     *       The chain awaits `Auth.whenSignedIn(30000)`, so this can outlast any
     *       fixed confirm delay — which is why this is a state check, not a timer.
     *   body.vault-gated     — the vault really is locked and the unlock modal
     *       is already on screen saying so. A banner behind that cover would
     *       only become readable once it had stopped being true.
     *   body.auth-gated / auth-resolving — the sign-in gate owns the page.
     *
     * In every one of these the user is already being told something truer than
     * this banner could say.
     */
    function _suppressed() {
        if (typeof document === 'undefined' || !document.body) return false;
        const c = document.body.classList;
        return c.contains('vault-restoring') || c.contains('vault-gated')
            || c.contains('auth-gated') || c.contains('auth-resolving');
    }

    /**
     * What is stopping writes right now.
     * Returns { blocked, reasons:[{key,label,action}], detail } — reasons is
     * ORDERED but complete, so both walls are named at once.
     */
    function assess() {
        if (_suppressed()) return { blocked: false, reasons: [] };
        const posture = window.SecurityPosture;
        if (!posture || typeof posture.getSnapshot !== 'function') return { blocked: false, reasons: [] };

        let snap;
        try { snap = posture.getSnapshot(); } catch (_) { return { blocked: false, reasons: [] }; }
        if (!snap || !snap.cloud) return { blocked: false, reasons: [] };

        // Signed out is the auth gate's job, not ours.
        if (!snap.cloud.signedIn) return { blocked: false, reasons: [] };
        // Sync switched off on this device is a CHOICE. Nagging about a setting
        // the user deliberately picked is how banners get ignored.
        if (snap.cloud.status === 'disabled') return { blocked: false, reasons: [] };

        const reasons = [];

        if (!snap.cloud.ok) {
            const vault = snap.vault || {};
            const mfa = snap.mfa || {};

            if (!vault.available) {
                reasons.push({ key: 'crypto', severity: 'gated', label: 'encryption is unavailable in this browser', action: null });
            } else if (!vault.enabled) {
                reasons.push({ key: 'vault-off', severity: 'gated', label: 'your encryption vault is not set up', action: 'vault' });
            } else if (!vault.unlocked) {
                reasons.push({ key: 'vault-locked', severity: 'gated', label: 'your vault is locked', action: 'vault' });
            }

            if (mfa.required && !mfa.verified) {
                reasons.push({ key: 'mfa', severity: 'gated', label: 'two-factor authentication is not set up', action: 'mfa' });
            }

            // Blocked for a reason we cannot name — still say so rather than
            // going quiet, because quiet is indistinguishable from working.
            //
            // This fallback MUST stay inside the !cloud.ok arm. Outside it,
            // every healthy account with nothing to report gets a permanent
            // unexplained banner.
            if (!reasons.length) {
                reasons.push({ key: 'other', severity: 'gated', label: snap.cloud.label || 'sync is blocked', action: null });
            }
        } else {
            // The third condition, and the only one evaluated when the gates
            // are CLEAR: writes are allowed, they have been tried, and they are
            // still not landing. Deliberately not evaluated while a gate is up
            // — _pushAllBlobs re-marks every present doc dirty before it bails
            // on a locked vault, so "27 changes have not reached the cloud"
            // beside "your vault is locked" would be two warnings for one
            // cause, and the count would be a fiction.
            const b = _backlogReason();
            if (b) reasons.push(b);
        }

        if (!reasons.length) return { blocked: false, reasons: [] };
        return { blocked: true, reasons };
    }

    /** "4 minutes ago" for the last retry attempt. Never a bare timestamp. */
    function _ago(ts) {
        const mins = Math.floor((Date.now() - ts) / 60000);
        if (mins < 1) return 'just now';
        if (mins === 1) return '1 minute ago';
        if (mins < 60) return `${mins} minutes ago`;
        const hrs = Math.round(mins / 60);
        return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
    }

    /**
     * Writes are allowed and the retry loop is still failing.
     *
     * Feature-detected on window.SyncRetry so this file keeps working (and
     * every existing test keeps passing) when the loop has not been init'd or
     * the module is absent. Only `lastOutcome === 'failed'` speaks: an idle,
     * gated or merely busy loop is not news.
     */
    function _backlogReason() {
        const r = window.SyncRetry;
        if (!r || typeof r.status !== 'function') return null;
        let s;
        try { s = r.status(); } catch (_) { return null; }
        if (!s || !s.pending || s.lastOutcome !== 'failed') return null;
        const when = s.lastAttemptAt ? _ago(s.lastAttemptAt) : 'not yet';
        return {
            key: 'backlog',
            severity: 'backlog',
            label: `${s.pending} change${s.pending === 1 ? '' : 's'} have not reached the cloud yet (last try ${when})`,
            action: 'retry',
        };
    }

    function _mount() {
        let el = document.getElementById(BANNER_ID);
        if (el) return el;
        // Inside the shell, not on <body>: the auth and vault gates hide
        // top-level chrome with a direct-child allow-list, so a body-level bar
        // would paint over the forced sign-in and unlock screens.
        const host = document.querySelector('.app-content')
            || document.getElementById('main-content')
            || document.body;
        if (!host) return null;
        el = document.createElement('div');
        el.id = BANNER_ID;
        el.className = 'sync-blocked-banner';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        host.insertBefore(el, host.firstChild);
        return el;
    }

    function _actionHtml(reasons) {
        const seen = new Set();
        const btns = [];
        reasons.forEach((r) => {
            if (!r.action || seen.has(r.action)) return;
            seen.add(r.action);
            if (r.action === 'vault') {
                btns.push('<button type="button" class="sync-blocked-btn" data-sync-fix="vault">Set up encryption</button>');
            } else if (r.action === 'mfa') {
                btns.push('<button type="button" class="sync-blocked-btn" data-sync-fix="mfa">Enable two-factor</button>');
            } else if (r.action === 'retry') {
                btns.push('<button type="button" class="sync-blocked-btn" data-sync-fix="retry">Try again now</button>');
            }
        });
        return btns.join('');
    }

    function render() {
        const state = assess();

        // Clearing is always immediate — a resolved problem must never linger.
        if (!state.blocked) {
            const existing = document.getElementById(BANNER_ID);
            if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
            _lastSig = '';
            _pendingSig = '';
            clearTimeout(_confirmTimer);
            _confirmTimer = null;
            return;
        }

        const sig = state.reasons.map(r => r.key).join('|');

        // First sighting of a blocked state: arm a confirm instead of painting.
        // See CONFIRM_MS — both inputs hydrate async, so the first reading after
        // boot is not yet trustworthy.
        if (sig !== _lastSig && sig !== _pendingSig && !document.getElementById(BANNER_ID)) {
            _pendingSig = sig;
            clearTimeout(_confirmTimer);
            _confirmTimer = setTimeout(() => {
                _confirmTimer = null;
                const still = assess();
                // Re-derive rather than trusting the armed signature: the state
                // may have changed shape (vault unlocked, MFA still missing).
                if (still.blocked) { _pendingSig = ''; _paint(still); }
                else { _pendingSig = ''; render(); }
            }, CONFIRM_MS);
            return;
        }

        _paint(state);
    }

    /** Render the banner for an already-confirmed blocked state. */
    function _paint(state) {
        const el = _mount();
        if (!el) return;

        const sig = state.reasons.map(r => r.key).join('|');
        const gated = state.reasons.some(r => r.severity !== 'backlog');

        const list = state.reasons.map(r => esc(r.label));
        const why = list.length === 1
            ? list[0]
            : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;

        // The backlog label carries a COUNT and an elapsed time, both of which
        // move. The signature is the bare key 'backlog' on purpose (encoding
        // the count would re-arm the 6s confirm on every change and churn), so
        // without this the first paint's numbers would be frozen forever.
        // Narrow on purpose: one textContent write, no innerHTML rewrite, so
        // nothing repaints under someone mid-read and the buttons keep their
        // identity.
        if (sig === _lastSig) {
            if (!gated) {
                const span = el.querySelector('.sync-blocked-text span');
                if (span) span.textContent = `${state.reasons[0].label}. They are saved on this device and the app keeps retrying on its own.`;
            }
            return; // don't repaint under someone mid-read
        }
        _lastSig = sig;

        // A backlog is less severe than "your work is only saved on this
        // device", and must not borrow that sentence: it is not true when the
        // gates are clear and the retry loop is still working the queue.
        el.setAttribute('data-severity', gated ? 'gated' : 'backlog');
        const headline = gated
            ? 'Your work is only saved on this device.'
            : 'Some changes have not reached the cloud yet.';
        const body = gated
            ? `Nothing is syncing because ${why}. If you clear your browser or change computer, this data is gone.`
            : `${why}. They are saved on this device and the app keeps retrying on its own.`;

        el.innerHTML = `
            <div class="sync-blocked-inner">
                <span class="sync-blocked-icon" aria-hidden="true">⚠</span>
                <div class="sync-blocked-text">
                    <strong>${headline}</strong>
                    <span>${body}</span>
                </div>
                <div class="sync-blocked-actions">${_actionHtml(state.reasons)}</div>
            </div>`;
    }

    function _onClick(e) {
        const btn = e.target && e.target.closest && e.target.closest('[data-sync-fix]');
        if (!btn) return;
        const what = btn.getAttribute('data-sync-fix');
        if (what === 'vault') {
            if (window.VaultUI && typeof VaultUI.forceUnlock === 'function') VaultUI.forceUnlock();
            else if (window.App && App.toast) App.toast('Open Settings → Security to set up your vault.', { type: 'error' });
        } else if (what === 'mfa') {
            // The only reliable caller of this in the app. See the header note.
            if (window.AuthMFAUI && typeof AuthMFAUI.openEnroll === 'function') AuthMFAUI.openEnroll();
            else if (window.App && App.toast) App.toast('Two-factor setup is unavailable right now.', { type: 'error' });
        } else if (what === 'retry') {
            if (window.SyncRetry && typeof SyncRetry.retryNow === 'function') SyncRetry.retryNow();
        }
        // Re-check shortly after: the user may have just fixed it.
        setTimeout(render, 1500);
    }

    function init() {
        if (typeof document === 'undefined') return;
        document.removeEventListener('click', _onClick); // init() can run twice
        document.addEventListener('click', _onClick);
        // The real signal. `vault:unlocked` is dispatched by VaultUI._emitUnlock
        // and already consumed by vault-gate.js, secure-storage.js,
        // dashboard-widgets.js and 13 others — re-checking here is the
        // codebase's own idiom and clears the banner the instant the vault
        // lands, rather than up to POLL_MS later.
        if (typeof window !== 'undefined') {
            window.removeEventListener('vault:unlocked', render);
            window.addEventListener('vault:unlocked', render);
        }
        render();
        clearInterval(_timer);
        _timer = setInterval(render, POLL_MS);
    }

    /** Tear down the poll + listener. Exists so tests don't leak a handle. */
    function stop() {
        clearInterval(_timer);
        clearTimeout(_confirmTimer);
        _timer = null;
        _confirmTimer = null;
        if (typeof document !== 'undefined') document.removeEventListener('click', _onClick);
        if (typeof window !== 'undefined') window.removeEventListener('vault:unlocked', render);
    }

    return {
        init, stop, render, assess,
        _BANNER_ID: BANNER_ID,
        _CONFIRM_MS: CONFIRM_MS,
        // Tests only: paint without waiting out the confirm delay.
        _paintNow: () => { const s = assess(); if (s.blocked) _paint(s); },
    };
})();
